import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { customElement, property, query, state, } from 'lit/decorators.js';
import { stylesBase, stylesEffects } from './styles';
import { emitEvent } from '../../utilities/misc/events';
/**
 * @since 1.0.0
 * @status stable
 *
 * @tagname kemet-modal
 * @summary A dialog that has many built-in effects and flexible styles.
 *
 * @prop {boolean} opened
 * @prop {string} effect
 * @prop {boolean} closeOnClick
 * @prop {string} breakpoint
 * @prop {boolean} mobile
 *
 * @csspart dialog - The main contents of the modal.
 * @csspart overlay - The surrounding scrim of the modal.
 *
 * @cssproperty --kemet-modal-radius - The mount of rounding for rounded corners
 * @cssproperty --kemet-modal-dialog-min-width - The minimum width of the dialog.
 * @cssproperty --kemet-modal-dialog-max-width - The maximum width of the dialog.
 * @cssproperty --kemet-modal-dialog-background-color - The background color of the dialog.
 * @cssproperty --kemet-modal-dialog-mobile-width - The width of the mobile dialog.
 * @cssproperty --kemet-modal-dialog-mobile-margin - The margins of the mobile dialog.
 * @cssproperty --kemet-modal-dialog-mobile-padding - The padding of the mobile dialog.
 * @cssproperty --kemet-modal-overlay-background-color - The color of the backdrop overlay.
 *
 * @event kemet-modal-opened - Fires when the modal opens
 * @event kemet-modal-closed - Fires when the modal closes
 *
 */
let KemetModal = class KemetModal extends LitElement {
    constructor() {
        super();
        this.opened = false;
        this.closeOnClick = false;
        this.breakpoint = '600px';
        // bindings
        this.addEventListener('kemet-modal-close-pressed', () => { this.handleClose(); });
    }
    firstUpdated() {
        // standard properties
        /** @internal */
        this.focusableSelector = 'body, a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, *[tabindex], *[contenteditable]';
        this.focusableElements = this.querySelectorAll(this.focusableSelector);
        // events
        this.addEventListener('keyup', (event) => {
            if (event.key === 'Escape') {
                this.handleClose();
            }
        });
        this.addEventListener('click', (event) => {
            const targetElement = event.target;
            if (this.opened && this.closeOnClick && targetElement.tagName.toLowerCase() === 'kemet-modal') {
                this.handleClose();
            }
        });
        window.addEventListener('resize', () => {
            this.isMobile();
        });
        this.focusableElements.forEach((element) => {
            element.addEventListener('keydown', (event) => this.handleFocusableDown(event));
        });
    }
    updated(prevProps) {
        if (!prevProps.get('opened') && this.opened === true) {
            this.handleOpen();
        }
        if (prevProps.get('opened') && this.opened === false) {
            this.handleClose();
        }
        this.isMobile();
    }
    render() {
        return html `
      <dialog part="dialog" @close=${() => this.handleClose()}>
        <slot></slot>
      </dialog>
      <div class="overlay" part="overlay"></div>
    `;
    }
    isMobile() {
        const mediaQuery = window.matchMedia(`(max-width: ${this.breakpoint})`);
        this.mobile = mediaQuery.matches;
    }
    handleOpen() {
        var _a;
        this.opened = true;
        if ((_a = this.dialogElement) === null || _a === void 0 ? void 0 : _a.showModal)
            this.dialogElement.showModal();
        emitEvent(this, 'kemet-modal-opened', this);
    }
    handleClose() {
        var _a;
        this.opened = false;
        if ((_a = this.dialogElement) === null || _a === void 0 ? void 0 : _a.close)
            this.dialogElement.close();
        emitEvent(this, 'kemet-modal-closed', this);
    }
    handleFocusableDown(event) {
        const firstFocusable = this.focusableElements[0];
        const lastFocusable = this.focusableElements[this.focusableElements.length - 1];
        if (event.key === 'Tab') {
            if (event.shiftKey && document.activeElement === firstFocusable) {
                event.preventDefault();
                lastFocusable.focus();
            }
            else if (!event.shiftKey && document.activeElement === lastFocusable) {
                event.preventDefault();
                firstFocusable.focus();
            }
        }
    }
};
KemetModal.styles = [stylesBase, stylesEffects];
__decorate([
    property({ type: Boolean, reflect: true })
], KemetModal.prototype, "opened", void 0);
__decorate([
    property({ type: String, reflect: true })
], KemetModal.prototype, "effect", void 0);
__decorate([
    property({ type: Boolean, attribute: 'close-on-click' })
], KemetModal.prototype, "closeOnClick", void 0);
__decorate([
    property({ type: String })
], KemetModal.prototype, "breakpoint", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], KemetModal.prototype, "mobile", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], KemetModal.prototype, "rounded", void 0);
__decorate([
    query('dialog')
], KemetModal.prototype, "dialogElement", void 0);
__decorate([
    state()
], KemetModal.prototype, "focusableSelector", void 0);
__decorate([
    state()
], KemetModal.prototype, "focusableElements", void 0);
KemetModal = __decorate([
    customElement('kemet-modal')
], KemetModal);
export default KemetModal;
//# sourceMappingURL=kemet-modal.js.map